import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  Button, 
  Input,
  Label,
  FormGroup,
  Select
} from '../../styles/StyledComponents';
import IconWrapper from '../common/IconWrapper';
import { 
  FaCog, 
  FaBell, 
  FaShieldAlt, 
  FaPalette, 
  FaKey,
  FaSave,
  FaTimes,
  FaCheck
} from 'react-icons/fa';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SettingsHeader = styled.div`
  margin-bottom: 2rem;
  
  h1 {
    color: var(--secondary-color);
    margin-bottom: 0.5rem;
    font-size: 1.8rem;
    
    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1.3rem;
    }
  }
  
  p {
    color: var(--dark-gray);
    font-size: 1.1rem;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
  }
`;

const SettingsCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  
  .settings-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
    
    .icon {
      color: var(--primary-color);
      font-size: 1.2rem;
    }
    
    h3 {
      color: var(--secondary-color);
      font-size: 1.2rem;
      margin: 0;
      
      @media (max-width: 768px) {
        font-size: 1.1rem;
      }
    }
  }
  
  .setting-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 0;
    border-bottom: 1px solid var(--light-gray);
    
    &:last-child {
      border-bottom: none;
    }
    
    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.75rem;
    }
    
    .setting-info {
      flex: 1;
      
      .setting-title {
        font-weight: 600;
        color: var(--secondary-color);
        margin-bottom: 0.25rem;
        font-size: 1rem;
        
        @media (max-width: 768px) {
          font-size: 0.9rem;
        }
      }
      
      .setting-description {
        color: var(--dark-gray);
        font-size: 0.9rem;
        line-height: 1.4;
        
        @media (max-width: 768px) {
          font-size: 0.85rem;
        }
      }
    }
    
    .setting-control {
      display: flex;
      align-items: center;
      gap: 1rem;
      
      @media (max-width: 768px) {
        align-self: flex-end;
      }
    }
  }
  
  .toggle-switch {
    position: relative;
    width: 50px;
    height: 24px;
    background: var(--medium-gray);
    border-radius: 12px;
    cursor: pointer;
    transition: background 0.3s ease;
    
    &.active {
      background: var(--primary-color);
    }
    
    .toggle-slider {
      position: absolute;
      top: 2px;
      left: 2px;
      width: 20px;
      height: 20px;
      background: white;
      border-radius: 50%;
      transition: transform 0.3s ease;
    }
    
    &.active .toggle-slider {
      transform: translateX(26px);
    }
  }
`;

const FormSection = styled.div`
  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 0.75rem;
    }
  }
  
  .form-actions {
    display: flex;
    gap: 1rem;
    margin-top: 1.5rem;
    
    @media (max-width: 768px) {
      flex-direction: column;
      gap: 0.75rem;
    }
  }
`;

const PasswordForm = styled.form`
  .password-requirements {
    margin-top: 1rem;
    padding: 1rem;
    background: var(--light-gray);
    border-radius: 8px;
    
    .requirement {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.5rem;
      font-size: 0.9rem;
      
      &:last-child {
        margin-bottom: 0;
      }
      
      @media (max-width: 768px) {
        font-size: 0.85rem;
      }
      
      .requirement-icon {
        color: var(--success-color);
        font-size: 0.8rem;
      }
      
      &.met {
        color: var(--success-color);
      }
      
      &.not-met {
        color: var(--dark-gray);
      }
    }
  }
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--dark-gray);
  
  .empty-icon {
    font-size: 4rem;
    color: var(--light-gray);
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
      font-size: 3rem;
    }
  }
  
  .empty-title {
    font-size: 1.3rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }
  
  .empty-description {
    font-size: 1rem;
    line-height: 1.6;
    max-width: 400px;
    margin: 0 auto;
    
    @media (max-width: 768px) {
      font-size: 0.9rem;
      max-width: 300px;
    }
  }
`;

const SupervisorSettings: React.FC = () => {
  const { user } = useAuth();
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    approvalReminders: true,
    projectUpdates: true,
    systemAlerts: false
  });

  const [securitySettings, setSecuritySettings] = useState({
    twoFactorAuth: false,
    sessionTimeout: '30',
    loginNotifications: true,
    passwordExpiry: '90'
  });

  const [appearanceSettings, setAppearanceSettings] = useState({
    theme: 'light',
    language: 'en',
    fontSize: 'medium',
    compactMode: false
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const handleNotificationToggle = (setting: string) => {
    setNotificationSettings(prev => ({
      ...prev,
      [setting]: !prev[setting as keyof typeof prev]
    }));
  };

  const handleSecurityToggle = (setting: string) => {
    setSecuritySettings(prev => ({
      ...prev,
      [setting]: !prev[setting as keyof typeof prev]
    }));
  };

  const handleAppearanceChange = (setting: string, value: string) => {
    setAppearanceSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would make an API call
    console.log('Changing password:', passwordForm);
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setShowPasswordForm(false);
  };

  const validatePassword = (password: string) => {
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    return requirements;
  };

  const passwordRequirements = validatePassword(passwordForm.newPassword);

  if (!user) {
    return (
      <PageContainer>
        <NoDataMessage>
          <div className="empty-icon">⚙️</div>
          <div className="empty-title">User Not Found</div>
          <div className="empty-description">
            Unable to load user settings. Please try again later.
          </div>
        </NoDataMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <SettingsHeader>
        <h1>Settings</h1>
        <p>Manage your account preferences and security settings</p>
      </SettingsHeader>

      <SettingsGrid>
        <SettingsCard>
          <div className="settings-header">
            <IconWrapper icon={FaBell} className="icon" />
            <h3>Notification Preferences</h3>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <div className="setting-title">Email Notifications</div>
              <div className="setting-description">Receive notifications via email</div>
            </div>
            <div className="setting-control">
              <div 
                className={`toggle-switch ${notificationSettings.emailNotifications ? 'active' : ''}`}
                onClick={() => handleNotificationToggle('emailNotifications')}
              >
                <div className="toggle-slider"></div>
              </div>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <div className="setting-title">SMS Notifications</div>
              <div className="setting-description">Receive notifications via SMS</div>
            </div>
            <div className="setting-control">
              <div 
                className={`toggle-switch ${notificationSettings.smsNotifications ? 'active' : ''}`}
                onClick={() => handleNotificationToggle('smsNotifications')}
              >
                <div className="toggle-slider"></div>
              </div>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <div className="setting-title">Push Notifications</div>
              <div className="setting-description">Receive in-app notifications</div>
            </div>
            <div className="setting-control">
              <div 
                className={`toggle-switch ${notificationSettings.pushNotifications ? 'active' : ''}`}
                onClick={() => handleNotificationToggle('pushNotifications')}
              >
                <div className="toggle-slider"></div>
              </div>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <div className="setting-title">Approval Reminders</div>
              <div className="setting-description">Get reminded about pending approvals</div>
            </div>
            <div className="setting-control">
              <div 
                className={`toggle-switch ${notificationSettings.approvalReminders ? 'active' : ''}`}
                onClick={() => handleNotificationToggle('approvalReminders')}
              >
                <div className="toggle-slider"></div>
              </div>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <div className="setting-title">Project Updates</div>
              <div className="setting-description">Receive project progress updates</div>
            </div>
            <div className="setting-control">
              <div 
                className={`toggle-switch ${notificationSettings.projectUpdates ? 'active' : ''}`}
                onClick={() => handleNotificationToggle('projectUpdates')}
              >
                <div className="toggle-slider"></div>
              </div>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <div className="setting-title">System Alerts</div>
              <div className="setting-description">Receive system maintenance alerts</div>
            </div>
            <div className="setting-control">
              <div 
                className={`toggle-switch ${notificationSettings.systemAlerts ? 'active' : ''}`}
                onClick={() => handleNotificationToggle('systemAlerts')}
              >
                <div className="toggle-slider"></div>
              </div>
            </div>
          </div>
        </SettingsCard>

        <SettingsCard>
          <div className="settings-header">
            <IconWrapper icon={FaShieldAlt} className="icon" />
            <h3>Security Settings</h3>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <div className="setting-title">Two-Factor Authentication</div>
              <div className="setting-description">Add an extra layer of security to your account</div>
            </div>
            <div className="setting-control">
              <div 
                className={`toggle-switch ${securitySettings.twoFactorAuth ? 'active' : ''}`}
                onClick={() => handleSecurityToggle('twoFactorAuth')}
              >
                <div className="toggle-slider"></div>
              </div>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <div className="setting-title">Session Timeout</div>
              <div className="setting-description">Automatically log out after inactivity</div>
            </div>
            <div className="setting-control">
              <Select
                value={securitySettings.sessionTimeout}
                onChange={(e) => setSecuritySettings(prev => ({ ...prev, sessionTimeout: e.target.value }))}
                style={{ minWidth: '120px' }}
              >
                <option value="15">15 minutes</option>
                <option value="30">30 minutes</option>
                <option value="60">1 hour</option>
                <option value="120">2 hours</option>
              </Select>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <div className="setting-title">Login Notifications</div>
              <div className="setting-description">Get notified of new login attempts</div>
            </div>
            <div className="setting-control">
              <div 
                className={`toggle-switch ${securitySettings.loginNotifications ? 'active' : ''}`}
                onClick={() => handleSecurityToggle('loginNotifications')}
              >
                <div className="toggle-slider"></div>
              </div>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <div className="setting-title">Password Expiry</div>
              <div className="setting-description">Days until password expires</div>
            </div>
            <div className="setting-control">
              <Select
                value={securitySettings.passwordExpiry}
                onChange={(e) => setSecuritySettings(prev => ({ ...prev, passwordExpiry: e.target.value }))}
                style={{ minWidth: '120px' }}
              >
                <option value="30">30 days</option>
                <option value="60">60 days</option>
                <option value="90">90 days</option>
                <option value="180">180 days</option>
              </Select>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <div className="setting-title">Change Password</div>
              <div className="setting-description">Update your account password</div>
            </div>
            <div className="setting-control">
              <Button onClick={() => setShowPasswordForm(!showPasswordForm)}>
                <IconWrapper icon={FaKey} />
                Change Password
              </Button>
            </div>
          </div>

          {showPasswordForm && (
            <PasswordForm onSubmit={handlePasswordSubmit}>
              <FormGroup>
                <Label>Current Password</Label>
                <Input
                  type="password"
                  name="currentPassword"
                  value={passwordForm.currentPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </FormGroup>

              <div className="form-row">
                <FormGroup>
                  <Label>New Password</Label>
                  <Input
                    type="password"
                    name="newPassword"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Confirm New Password</Label>
                  <Input
                    type="password"
                    name="confirmPassword"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </FormGroup>
              </div>

              <div className="password-requirements">
                <div className="requirement">
                  <IconWrapper icon={FaCheck} className="requirement-icon" />
                  Password Requirements:
                </div>
                <div className={`requirement ${passwordRequirements.length ? 'met' : 'not-met'}`}>
                  <IconWrapper icon={passwordRequirements.length ? FaCheck : FaTimes} className="requirement-icon" />
                  At least 8 characters
                </div>
                <div className={`requirement ${passwordRequirements.uppercase ? 'met' : 'not-met'}`}>
                  <IconWrapper icon={passwordRequirements.uppercase ? FaCheck : FaTimes} className="requirement-icon" />
                  One uppercase letter
                </div>
                <div className={`requirement ${passwordRequirements.lowercase ? 'met' : 'not-met'}`}>
                  <IconWrapper icon={passwordRequirements.lowercase ? FaCheck : FaTimes} className="requirement-icon" />
                  One lowercase letter
                </div>
                <div className={`requirement ${passwordRequirements.number ? 'met' : 'not-met'}`}>
                  <IconWrapper icon={passwordRequirements.number ? FaCheck : FaTimes} className="requirement-icon" />
                  One number
                </div>
                <div className={`requirement ${passwordRequirements.special ? 'met' : 'not-met'}`}>
                  <IconWrapper icon={passwordRequirements.special ? FaCheck : FaTimes} className="requirement-icon" />
                  One special character
                </div>
              </div>

              <div className="form-actions">
                <Button type="submit" style={{ backgroundColor: 'var(--success-color)' }}>
                  <IconWrapper icon={FaSave} />
                  Update Password
                </Button>
                <Button type="button" onClick={() => setShowPasswordForm(false)} style={{ backgroundColor: 'var(--danger-color)' }}>
                  <IconWrapper icon={FaTimes} />
                  Cancel
                </Button>
              </div>
            </PasswordForm>
          )}
        </SettingsCard>

        <SettingsCard>
          <div className="settings-header">
            <IconWrapper icon={FaPalette} className="icon" />
            <h3>Appearance Settings</h3>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <div className="setting-title">Theme</div>
              <div className="setting-description">Choose your preferred color theme</div>
            </div>
            <div className="setting-control">
              <Select
                value={appearanceSettings.theme}
                onChange={(e) => handleAppearanceChange('theme', e.target.value)}
                style={{ minWidth: '120px' }}
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
                <option value="auto">Auto</option>
              </Select>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <div className="setting-title">Language</div>
              <div className="setting-description">Select your preferred language</div>
            </div>
            <div className="setting-control">
              <Select
                value={appearanceSettings.language}
                onChange={(e) => handleAppearanceChange('language', e.target.value)}
                style={{ minWidth: '120px' }}
              >
                <option value="en">English</option>
                <option value="sw">Swahili</option>
              </Select>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <div className="setting-title">Font Size</div>
              <div className="setting-description">Adjust the text size</div>
            </div>
            <div className="setting-control">
              <Select
                value={appearanceSettings.fontSize}
                onChange={(e) => handleAppearanceChange('fontSize', e.target.value)}
                style={{ minWidth: '120px' }}
              >
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
              </Select>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <div className="setting-title">Compact Mode</div>
              <div className="setting-description">Use a more compact layout</div>
            </div>
            <div className="setting-control">
              <div 
                className={`toggle-switch ${appearanceSettings.compactMode ? 'active' : ''}`}
                onClick={() => setAppearanceSettings(prev => ({ ...prev, compactMode: !prev.compactMode }))}
              >
                <div className="toggle-slider"></div>
              </div>
            </div>
          </div>
        </SettingsCard>
      </SettingsGrid>
    </PageContainer>
  );
};

export default SupervisorSettings; 